import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import Hero from '../components/Hero';
import './Contact.css';

const Contact: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await emailjs.send('service_42xg2kk', 'template_acmc7bi', {
        from_name: form.name,
        company: form.company || 'N/A',
        from_email: form.email,
        phone: form.phone || 'N/A',
        message: form.message,
      }, 'jIWhQ6wtDYBMCRy0o');
      setSubmitted(true);
    } catch {
      setError('Failed to send message. Please try again or email us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <Hero
        title="Contact Us"
        subtitle="Get in touch for product information, pricing, technical support, or to set up a trade account."
        background="gradient-dark"
        fullHeight={false}
      />

      <section className="section section--dark">
        <div className="container">
          <div className="contact__layout">
            {/* Form */}
            <div className="contact__form-wrapper">
              {submitted ? (
                <div className="contact__success">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <h3>Message Sent</h3>
                  <p>Thank you for reaching out. We'll get back to you within one business day.</p>
                  <button
                    className="btn btn--outline-teal"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', company: '', phone: '', email: '', message: '' });
                    }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact__form">
                  <div className="contact__field">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="contact__field">
                    <label htmlFor="company">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Your company name"
                    />
                  </div>
                  <div className="contact__row">
                    <div className="contact__field">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="you@company.com"
                      />
                    </div>
                    <div className="contact__field">
                      <label htmlFor="phone">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="(555) 000-0000"
                      />
                    </div>
                  </div>
                  <div className="contact__field">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us about your project or what you need..."
                    />
                  </div>
                  {error && <p className="contact__error">{error}</p>}
                  <button type="submit" className="btn btn--primary btn--lg" disabled={sending}>
                    {sending ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="contact__info">
              <div className="contact__info-card">
                <h3>Get in Touch</h3>
                <div className="contact__info-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <div>
                    <span className="contact__info-label">Email</span>
                    <a href="mailto:info@lexingtonairsystems.com" className="contact__info-value">
                      info@lexingtonairsystems.com
                    </a>
                  </div>
                </div>
                <div className="contact__info-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2">
                    <circle cx="12" cy="10" r="3" />
                    <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" />
                  </svg>
                  <div>
                    <span className="contact__info-label">Address</span>
                    <span className="contact__info-value">25-C Olympia Avenue, Woburn, MA 01810</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
